import moment from "moment-timezone";

// Date-only picker keeps the initial time. For today, push to end-of-day
// so the package doesn't expire before the save round-trip completes.
const time = (value: any) => {
  if (!value) return null;
  const picked = moment(value);
  return (picked.isSame(moment(), "day") ? picked.endOf("day") : picked).valueOf();
};

/**
 * Debug and Trial packages share the same shape: a single expiration
 * timestamp field on the site, defaulted by the backend when enabled with
 * no explicit date, and cleared (not just flagged off) when disabled.
 */
const makeTemporaryPackageField = (fieldName: string, payloadKey: string, defaultDays: number) => {
  const defaultExpiresAt = (now: Date = moment().toDate()) =>
    moment(now).add(defaultDays, "days").toDate();

  const isActive = (site: any, now: Date = moment().toDate()) =>
    !!site?.[fieldName] && site[fieldName] > now.getTime();

  /**
   * Use the stored expiration's day with the current time-of-day.
   * Avoids carrying over an end-of-day time from a previous "today" save.
   * Otherwise defaults to `defaultDays` from now.
   */
  const initialExpiresAt = (site: any, now: Date = moment().toDate()) =>
    isActive(site, now)
      ? moment(now)
          .set({
            year: moment(site[fieldName]).year(),
            month: moment(site[fieldName]).month(),
            date: moment(site[fieldName]).date()
          })
          .toDate()
      : defaultExpiresAt(now);

  /**
   * Builds the API payload. Clears when unchecked; sends changed dates;
   * otherwise only lets the backend default when the package was just enabled.
   * Omit untouched active packages to preserve their existing expiration.
   */
  const apply = (
    siteData: any,
    enabled: boolean,
    expiresAtValue: any,
    initialExpiresAtValue: any,
    initiallyEnabled: boolean
  ) => {
    const next = { ...siteData };
    delete next[fieldName];

    const chosen = time(expiresAtValue);
    const dateChanged = chosen !== time(initialExpiresAtValue);

    if (enabled === initiallyEnabled && !dateChanged) {
      return next;
    }

    if (!enabled) {
      next[payloadKey] = { enabled: false };
      return next;
    }

    next[payloadKey] = dateChanged
      ? { enabled: true, expiresAt: chosen }
      : { enabled: true };

    return next;
  };

  return { isActive, initialExpiresAt, apply };
};

export const debugPackage = makeTemporaryPackageField("debugPackageExpiresAt", "debugPackage", 14);
export const trialPackage = makeTemporaryPackageField("trialPackageExpiresAt", "trialPackage", 30);
